<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>{{ website_name }} Listeners</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="listeners"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1], // -> Add this example
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Item</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row>
                        <v-text-field
                          v-model="editedItem.addr"
                          label="Address*"
                          :rules="ipRules"
                          required
                        ></v-text-field>
                      </v-row>
                      <v-row>
                        <v-text-field
                          type="number" 
                          v-model="editedItem.port"
                          label="Port*"
                          :rules="validPort"
                          required
                        ></v-text-field>
                      </v-row>
                      <v-row>
                        <v-select
                          v-model="editedItem.protocol"
                          :items="transformedProtocols"
                          label="Protocol*"
                          item-text="text"
                          item-value="value"
                          editable
                          required
                          :rules="[(v) => !!v || 'Value is required']"
                          />
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="deleteItem(item)" v-on="on">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import httpProtocols from "@/common/config/http_protocols";
import ipAddr from "ip-address";


export default {
  mounted() {
    let website_id = this.$route.params.website_id;

    api
      .query(`beta/waf/websites/${website_id}/listeners`)
      .then((response) => (this.listeners = response.data.listeners))
      .catch((error) => (this.errors = [error]))
      .finally(() => (this.loading = false));
    
    this.website_name = this.$store.state.web.website_name;
  },

  data: () => ({
    selected: [],
    headers: [
      {
        text: "ID",
        align: "left",
        sortable: false,
        value: "id",
      },
      { text: "Address", value: "addr" },
      { text: "Port", value: "port" },
      { text: "Protocol", value: "protocol" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    listeners: [],
    protocols: httpProtocols,
    errors: [],
    loading: true,
    dialog: false,
    editedItem: {
      id: "",
      addr: "",
      port: null,
      protocol: null,
    },
    defaultItem: {
      id: "",
      addr: "",
      port: null,
      protocol: null,
    },
    search: "",
    ipRules: [
      (v) => !!v || "IP address is required",
      (v) =>
        (!!v &&
          (new ipAddr.Address4(v).isValid() ||
            new ipAddr.Address6(v).isValid())) ||
        "IP address is invalid",
    ],
    validPort: [
      (v) => !!v || "Port is required",
      (v) =>
        !v ||
        (parseInt(v) !== NaN && parseInt(v) > 0 && parseInt(v) < 65536) ||
        "Must be between 1 and 65535",
    ],
    website_name: "",
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    deleteItem(item) {
      let website_id = this.$route.params.website_id;
      if (confirm("Are you sure you want to delete this item?")) {
        api
          .delete(`beta/waf/websites/${website_id}/listeners/${item.id}`)
          .then((response) => {
            const index = this.listeners.indexOf(item);
            this.listeners.splice(index, 1);
          });
      }
    },
    
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;

      let website_id = this.$route.params.website_id;

      api
        .post(`beta/waf/websites/${website_id}/listeners`, {
          addr: this.editedItem.addr,
          port: this.editedItem.port,
          protocol: this.editedItem.protocol,
        })
        .then((response) => {
          this.listeners = response.data.listeners;
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
    
      this.close();
    },
  },

  computed: {
    formTitle() {
      return "New Item";
    },
    transformedProtocols() {
      return this.protocols.map(protocol => {
        const key = Object.keys(protocol)[0];
        return { text: protocol[key], value: key };
      });
    }
  },
};
</script>
